<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

interface CategoryItem {
  id: string | number | null;
  name: string;
}

const props = defineProps<{
  categories: CategoryItem[];
  modelValue: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', id: string | null): void;
}>();
const { t } = useI18n();

const tabsRef = ref<HTMLElement | null>(null);

const normalizeCategoryId = (id: unknown): string => String(id ?? '').trim();

const selectCategory = (id: string | number | null, event?: MouseEvent) => {
  const normalizedId = id == null ? null : normalizeCategoryId(id);
  emit('update:modelValue', normalizedId);
  
  if (event && event.currentTarget) {
    const target = event.currentTarget as HTMLElement;
    if (typeof target?.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }
};

// Initial scroll to active category
onMounted(() => {
  nextTick(() => {
    if (props.modelValue !== null && tabsRef.value) {
      const activeTab = tabsRef.value.querySelector('[role="tab"].is-active') as HTMLElement;
      if (activeTab && typeof activeTab.scrollIntoView === 'function') {
        activeTab.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
      }
    }
  });
});

// Keyboard navigation for tablist
const handleArrowRight = () => {
  if (!tabsRef.value) return;
  const tabs = Array.from(tabsRef.value.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  if (tabs.length === 0) return;
  const currentIndex = tabs.findIndex(t => t.classList.contains('is-active'));
  const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % tabs.length;
  tabs[nextIndex].focus();
  selectCategory(tabIdFromElement(tabs[nextIndex]));
};

const handleArrowLeft = () => {
  if (!tabsRef.value) return;
  const tabs = Array.from(tabsRef.value.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  if (tabs.length === 0) return;
  const currentIndex = tabs.findIndex(t => t.classList.contains('is-active'));
  const prevIndex = currentIndex < 0 ? 0 : (currentIndex - 1 + tabs.length) % tabs.length;
  tabs[prevIndex].focus();
  selectCategory(tabIdFromElement(tabs[prevIndex]));
};

const tabIdFromElement = (el: Element): string | null => {
  const btn = (el.closest('button') || el) as HTMLElement;
  const catId = btn.getAttribute('data-category-id');
  if (catId === 'all' || !catId) return null;
  return catId;
};
</script>

<template>
  <nav ref="tabsRef" class="category-tabs" aria-label="Categorías del menú">
    <div
      class="category-tabs__scroll"
      role="tablist"
      aria-label="Filtrar por categoría"
      @keydown.arrow-right.prevent="handleArrowRight"
      @keydown.arrow-left.prevent="handleArrowLeft"
    >
      <button
        role="tab"
        :class="['category-tab', { 'is-active': modelValue === null }]"
        :aria-selected="modelValue === null"
        :aria-pressed="modelValue === null"
        aria-label="Ver todos los productos"
        :tabindex="modelValue === null ? 0 : -1"
        data-category-id="all"
        @click="selectCategory(null, $event)"
        @keydown.enter.prevent="selectCategory(null, $event)"
        @keydown.space.prevent="selectCategory(null, $event)"
      >
        <span>{{ t('clients.menu.all') }}</span>
      </button>

      <button
        v-for="category in categories"
        :key="String(category.id ?? category.name)"
        role="tab"
        class="category-tab"
        :class="{ 'is-active': modelValue === normalizeCategoryId(category.id) }"
        :aria-selected="modelValue === normalizeCategoryId(category.id)"
        :aria-pressed="modelValue === normalizeCategoryId(category.id)"
        :aria-label="`Ver ${category.name}`"
        :tabindex="modelValue === normalizeCategoryId(category.id) ? 0 : -1"
        :data-category-id="normalizeCategoryId(category.id)"
        @click="selectCategory(category.id, $event)"
        @keydown.enter.prevent="selectCategory(category.id, $event)"
        @keydown.space.prevent="selectCategory(category.id, $event)"
      >
        <span>{{ category.name }}</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.category-tabs {
  position: sticky;
  top: 0;
  z-index: 20;
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  padding: 1rem 1.5rem;
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .category-tabs {
    padding: 1rem;
  }
}

.category-tabs__scroll {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 0;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.category-tab {
  flex: 0 0 auto;
  padding: 0.6rem 1.5rem;
  border-radius: 999px;
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.01);
}

.category-tab:hover {
  background: #f8fafc;
  color: #334155;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.category-tab.is-active {
  background: var(--primary, #c2410c);
  border-color: var(--primary, #c2410c);
  color: white;
  box-shadow: 0 4px 12px rgba(194, 65, 12, 0.35);
  transform: translateY(-1px);
}

.category-tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(194, 65, 12, 0.4);
}

.category-tab.is-active:focus-visible {
  box-shadow: 0 0 0 3px rgba(194, 65, 12, 0.4), 0 4px 12px rgba(194, 65, 12, 0.35);
}

@media (max-width: 768px) {
  .category-tabs__scroll {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 0.2rem;
  }

  .category-tabs__scroll::-webkit-scrollbar {
    display: none;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .category-tab {
    transition: none !important;
  }
  .category-tab:hover,
  .category-tab.is-active {
    transform: none !important;
  }
}
</style>
